import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { Button, ErrorNote, Field, Input } from "../components/ui";
import { AuthFrame, CompactWordmark } from "../components/Brand";

const MIN_PASSWORD = 12;

/**
 * Setting the new password, from the emailed link.
 *
 * It ends at the sign-in screen rather than signing anybody in. An account with
 * a second factor would otherwise have it bypassed by whoever reached the
 * mailbox, which is the whole thing the second factor is there to stop.
 */
export function ResetPassword() {
  const { token = "" } = useParams();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [done, setDone] = useState(false);

  const link = useQuery({
    queryKey: ["reset", token],
    queryFn: () => api<{ email: string }>(`/api/v1/auth/reset-password/${token}`),
    retry: false,
    enabled: !done,
  });

  const save = useMutation({
    mutationFn: () =>
      api<void>("/api/v1/auth/reset-password", { method: "POST", body: { token, password } }),
    onSuccess: () => setDone(true),
  });

  const mismatch = confirmation.length > 0 && confirmation !== password;
  const ready = password.length >= MIN_PASSWORD && confirmation === password;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (ready) save.mutate();
  }

  if (done) {
    return (
      <Frame title="Password changed">
        <p className="mt-6 max-w-prose text-sm leading-relaxed text-muted">
          Sign in with your new password. Everywhere that was signed in as you has been signed out.
        </p>
        <div className="mt-8">
          <Link to="/login">
            <Button variant="primary" className="min-h-[2.75rem]">
              Go to sign in
            </Button>
          </Link>
        </div>
      </Frame>
    );
  }

  if (link.isPending) {
    return (
      <Frame title="One moment">
        <p className="mt-6 text-sm text-dim">Checking your link…</p>
      </Frame>
    );
  }

  if (link.isError) {
    // Unknown, expired and already used all land here, because the API gives
    // one answer for all three.
    return (
      <Frame title="This link has expired">
        <p className="mt-6 max-w-prose text-sm leading-relaxed text-muted">
          Reset links work once and last an hour. Ask for a new one and it will still be quick.
        </p>
        <div className="mt-8">
          <Link to="/forgot-password">
            <Button variant="primary" className="min-h-[2.75rem]">
              Send a new link
            </Button>
          </Link>
        </div>
      </Frame>
    );
  }

  return (
    <Frame title="Choose a new password">
      <p className="mt-2 break-words text-sm text-muted">
        For <span className="text-ink">{link.data.email}</span>.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <Field label="New password" hint={`At least ${MIN_PASSWORD} characters.`}>
          <Input
            type="password"
            value={password}
            autoComplete="new-password"
            required
            autoFocus
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <Field label="Type it again">
          <Input
            type="password"
            value={confirmation}
            autoComplete="new-password"
            required
            onChange={(event) => setConfirmation(event.target.value)}
          />
        </Field>

        {mismatch && <ErrorNote>Those two do not match.</ErrorNote>}
        {save.isError && (
          <ErrorNote>
            {save.error instanceof ApiError
              ? save.error.message
              : "Something went wrong. Try again."}
          </ErrorNote>
        )}

        <Button
          type="submit"
          variant="primary"
          className="min-h-[2.75rem] w-full"
          disabled={!ready || save.isPending}
        >
          {save.isPending ? "Saving…" : "Set my password"}
        </Button>
      </form>
    </Frame>
  );
}

function Frame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <AuthFrame>
      <header className="space-y-2">
        <CompactWordmark />
        <h1 className="display pt-4 text-[2.5rem] text-ink lg:pt-0">{title}</h1>
      </header>
      {children}
    </AuthFrame>
  );
}
